import { useEffect, useState, useMemo } from 'react';
import { useLiveEvents } from '../hooks/useLiveEvents.js';
import { useEventFilter, WINDOW_OPTIONS } from '../hooks/useEventFilter.js';
import { EventCard } from '../components/EventCard.jsx';
import { FilterToggles } from '../components/FilterToggles.jsx';
import { TapeStatsBar } from '../components/TapeStatsBar.jsx';
import { RecipientTimeline } from '../components/RecipientTimeline.jsx';
import { ActivityFeed } from '../components/ActivityFeed.jsx'; // [NEW FEATURE 10]

function recipientKey(e) {
  return e.contact_email || e.recipient || e.recipient_email || e.recipient_view_id || e.view_id || null;
}

export function LiveFeed() {
  const [filter, setFilter] = useState('all');
  const [recipientFilter, setRecipientFilter] = useState('');
  // [RAW DEFAULTS] Show every event by default — the operator decides what's
  // signal vs noise. Filters are opt-in checkboxes, not opt-out defaults.
  const [hideBots, setHideBots] = useState(false);
  const [hidePrefetch, setHidePrefetch] = useState(false);
  const [windowKey, setWindowKey] = useState('24h');
  const [activeRecipient, setActiveRecipient] = useState(null);
  const { events, loading } = useLiveEvents({ filter, max: 500 });

  // [v3 polish] 1: tick a state value every 30s so children that render
  // relative timestamps ("5m ago") re-evaluate and update without a refresh.
  // We pass `nowTick` through as a key suffix so memoised children invalidate.
  const [, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // 1. Apply the time window first (everything else respects it).
  const windowed = useEventFilter(events, windowKey);

  // 2. Recipient + bot/prefetch filters layered on top.
  const filtered = useMemo(() => {
    let xs = windowed;
    if (recipientFilter.trim()) {
      const f = recipientFilter.toLowerCase();
      xs = xs.filter((e) => (recipientKey(e) || '').toLowerCase().includes(f));
    }
    if (hideBots) {
      // [hardening 3x] also strip ua_bucket bot/scanner so events whose
      // classification field hasn't been backfilled still get hidden.
      xs = xs.filter((e) =>
        !['bot','prefetch','apple_prefetch','likely_bot'].includes(e.classification)
        && !['bot','scanner','prefetch'].includes(e.ua_bucket),
      );
    } else if (hidePrefetch) {
      // Independent of "hide bots" — prefetch is the noisiest single class so
      // we let users hide JUST that without also nuking confirmed_human edge cases.
      xs = xs.filter((e) =>
        e.classification !== 'prefetch'
        && e.classification !== 'apple_prefetch'
        && e.ua_bucket !== 'prefetch',
      );
    }
    return xs;
  }, [windowed, recipientFilter, hideBots, hidePrefetch]);

  // 3. Events for the modal — pulled from the full live stream so the timeline
  //    isn't accidentally truncated by an outer filter the user toggled on.
  const recipientEvents = useMemo(() => {
    if (!activeRecipient) return [];
    return events.filter((e) => recipientKey(e) === activeRecipient);
  }, [events, activeRecipient]);

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          Live feed{' '}
          <span style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 400 }}>
            · {filtered.length}/{events.length} events
          </span>
        </h2>
        <FilterToggles value={filter} onChange={setFilter} />
      </div>

      {/* Time-window chip strip */}
      <WindowChips value={windowKey} onChange={setWindowKey} />

      {!loading && events.length > 0 && (
        <TapeStatsBar events={filtered} onRecipientClick={setActiveRecipient} />
      )}

      {/* [NEW FEATURE 10] Recent activity rollup. */}
      <div className="mb-4">
        <ActivityFeed limit={20} />
      </div>

      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14,
        padding: 8, background: 'var(--sunken)', border: '1px solid var(--rule)',
        borderRadius: 'var(--radius)',
      }}>
        <input
          value={recipientFilter}
          onChange={(e) => setRecipientFilter(e.target.value)}
          placeholder="Filter by recipient email…"
          style={{ flex: 1, padding: '6px 10px', fontSize: 12 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--body)', fontSize: 12 }}>
          <input type="checkbox" checked={hideBots} onChange={(e) => setHideBots(e.target.checked)} />
          Hide bots
        </label>
        {/* [v3 polish] 10: dedicated prefetch toggle — on by default. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--body)', fontSize: 12 }}>
          <input type="checkbox" checked={hidePrefetch} onChange={(e) => setHidePrefetch(e.target.checked)} />
          Hide prefetch
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-zinc-500">
          {events.length === 0
            ? 'No events yet. Send a tracked email or click a tracked link to populate.'
            : 'No events match the current filter.'}
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((ev) => {
            const k = recipientKey(ev);
            return (
              <ClickableRow key={ev.id}
                            disabled={!k}
                            onClick={() => k && setActiveRecipient(k)}>
                <EventCard event={ev} />
              </ClickableRow>
            );
          })}
        </div>
      )}

      {activeRecipient && (
        <RecipientTimeline
          recipient={activeRecipient}
          events={recipientEvents}
          onClose={() => setActiveRecipient(null)}
        />
      )}
    </section>
  );
}

function WindowChips({ value, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap',
    }}>
      <span className="eyebrow" style={{ fontSize: 9, marginRight: 4 }}>Window</span>
      {WINDOW_OPTIONS.map((w) => {
        const active = value === w.id;
        return (
          <button
            key={w.id}
            onClick={() => onChange(w.id)}
            style={{
              padding: '4px 10px',
              fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
              border: '1px solid var(--rule)',
              background: active ? 'var(--btn-bg)' : 'transparent',
              color: active ? 'var(--btn-fg)' : 'var(--muted)',
              fontWeight: active ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {w.label}
          </button>
        );
      })}
    </div>
  );
}

// ClickableRow — wraps an EventCard so the entire row opens the timeline.
// EventCard itself owns an internal expand/collapse button; we listen on the
// outer wrapper and gate via the originating element so the inner toggle
// keeps working as before. Click on padding/whitespace also opens the modal.
function ClickableRow({ children, onClick, disabled }) {
  const handle = (e) => {
    if (disabled) return;
    // If the user clicked a real interactive element inside the card
    // (the expand/collapse button, a link), let that handler run instead.
    const tgt = e.target;
    if (tgt.closest('a, input, textarea, select')) return;
    // EventCard's outer button toggles details; double-tap (shift-click)
    // opens the modal so we don't steal the existing UX. Plain click on the
    // surrounding wrapper area still opens the timeline.
    if (tgt.closest('button')) {
      // Allow ctrl/meta/shift modifier to deep-link the recipient.
      if (!(e.metaKey || e.ctrlKey || e.shiftKey)) return;
    }
    e.preventDefault();
    onClick?.();
  };
  return (
    <div
      onClick={handle}
      style={{ cursor: disabled ? 'default' : 'pointer', position: 'relative' }}
      title={disabled ? '' : 'Shift-click row, or click the recipient chip, to open timeline'}
    >
      {children}
      {!disabled && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--muted)', pointerEvents: 'none',
          padding: '2px 6px', border: '1px solid var(--rule)',
          background: 'var(--card)', opacity: 0.85,
        }}>
          ⇧ Click → timeline
        </div>
      )}
    </div>
  );
}
